import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckSquare } from 'lucide-react';
import { useNotepadDrawer } from './useNotepadDrawer';
import { useMyTasks } from '@/hooks/useMyTasks';
import { useNotepadEnabled } from '@/hooks/useUserFlag';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { startOfDay, addDays } from 'date-fns';

/**
 * Notepad button for top bar
 * Shows badge with count of overdue + due today tasks
 * Keyboard shortcut: Cmd/Ctrl+J
 */
export function NotepadButton() {
  const { t } = useTranslation(['notepad', 'common']);
  const { open } = useNotepadDrawer();
  const isEnabled = useNotepadEnabled();

  // Query overdue + today tasks
  const today = startOfDay(new Date());
  const tomorrow = addDays(today, 1);

  const { data: tasks } = useMyTasks({
    windowEnd: tomorrow,
    statuses: ['open', 'in_progress', 'blocked'],
  });

  // Don't render if feature is disabled
  if (!isEnabled) {
    return null;
  }

  // Count tasks that need attention
  const urgentCount = tasks?.length || 0;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={open}
            aria-label={t('accessibility.openNotepad')}
            className="relative inline-flex h-9 w-9 items-center justify-center rounded-xl border border-border hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <CheckSquare size={18} />
            {urgentCount > 0 && (
              <Badge
                variant="destructive"
                className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 flex items-center justify-center text-[10px] font-bold"
              >
                {urgentCount > 99 ? '99+' : urgentCount}
              </Badge>
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <div className="flex flex-col gap-1">
            <span>{t('notepad:drawer.title')}</span>
            <kbd className="text-xs opacity-70">
              {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+J
            </kbd>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
